import React, { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Clock } from "lucide-react";
import { Hero } from "@/components/sections/Hero";
import { CTABanner } from "@/components/sections/CTABanner";
import { Reveal } from "@/components/ui/Reveal";
import { StatCounter } from "@/components/ui/StatCounter";
import { SectionHeading, RichText } from "@/components/ui/RichText";
import { Pagination } from "@/components/ui/Paginator";
import { LinkedInButton } from "@/components/ui/LinkedInButton";
import { Seo } from "@/components/ui/Seo";
import { ErrorBanner, EmptyState } from "@/components/ui/States";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useFetch } from "@/hooks/useFetch";
import { localizedPath, pick } from "@/lib/helpers";
import { mediaUrl } from "@/lib/api";

function TeamCard({ member, lang, onOpen }) {
  const { t } = useTranslation();
  return (
    <div className="text-center" data-testid={`team-card`}>
      <div className="relative mx-auto w-40 h-40 overflow-hidden rounded-full border border-hair">
        <img
          src={mediaUrl(member.photoUrl, { w: 300, fmt: "webp", q: 80 })}
          alt={member.name}
          loading="lazy"
          decoding="async"
          width="160"
          height="160"
          className="w-full h-full object-cover"
        />
      </div>
      <h3 className="mt-5 font-display font-bold uppercase text-fg text-lg tracking-wide">{member.name}</h3>
      <p className="mt-1 font-techmono text-xs uppercase tracking-wider text-accent">
        {pick({ en: member.titleEn, fr: member.titleFr }, lang)}
      </p>
      <div className="mt-3 flex items-center justify-center gap-3">
        {(member.bioEn || member.bioFr) ? (
          <button
            type="button"
            onClick={() => onOpen(member)}
            className="font-display uppercase tracking-widest text-xs text-fg hover:text-accent transition-colors"
            data-testid="team-view-bio"
          >
            {t("cta.viewBio")}
          </button>
        ) : null}
        <LinkedInButton url={member.linkedinUrl} label={`${member.name} LinkedIn`} className="w-9 h-9" testId="team-linkedin" />
      </div>
    </div>
  );
}

export default function About() {
  const { lang = "en" } = useParams();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const teamPage = Math.max(1, parseInt(params.get("page") || "1", 10));
  const [bio, setBio] = useState(null);

  const { data: page, error, refetch } = useFetch(`/pages/about`, []);
  const { data: team } = useFetch(`/team?page=${teamPage}&perPage=8`, [teamPage]);
  const { data: timeline } = useFetch(`/timeline`, []);
  const { data: awards } = useFetch(`/awards`, []);

  // Never block the page on a single failed fetch; show inline retry instead.

  const teamItems = team?.items || [];
  const timelineItems = timeline || [];
  const awardItems = awards || [];

  return (
    <>
      <Seo
        title={pick(page?.seo?.metaTitle, lang) || t("nav.about")}
        description={pick(page?.seo?.metaDescription, lang)}
        lang={lang}
        path={localizedPath(lang, "/about")}
        image={mediaUrl(page?.heroImageUrl)}
      />

      <Hero
        eyebrow={pick(page?.heroEyebrow, lang)}
        title={pick(page?.heroTitle, lang) || t("nav.about")}
        subtitle={pick(page?.heroSubtitle, lang)}
        image={page?.heroImageUrl}
      />

      {error && !page ? <ErrorBanner onRetry={refetch} testId="about-content-error" /> : null}

      {/* Story + mission */}
      {(page?.story || page?.mission) ? (
        <section className="container-x py-20 md:py-28" data-testid="about-story">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <Reveal>
              <SectionHeading eyebrow={pick(page.storyTitle, lang)} title={pick(page.missionTitle, lang)} />
              <div className="mt-8 space-y-6">
                <RichText html={pick(page.story, lang)} />
                <RichText html={pick(page.mission, lang)} />
              </div>
            </Reveal>
            {page.storyImageUrl ? (
              <Reveal delay={0.15}>
                <div className="relative">
                  <div className="absolute -inset-3 border border-cyan/20" />
                  <img
                    src={mediaUrl(page.storyImageUrl, { w: 800, fmt: "webp", q: 78 })}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width="800"
                    className="relative w-full aspect-[3/4] object-cover"
                  />
                </div>
              </Reveal>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* Values */}
      {page?.values?.length ? (
        <section className="bg-surface border-y border-hair py-20 md:py-28" data-testid="about-values">
          <div className="container-x">
            <SectionHeading title={pick(page.valuesTitle, lang)} />
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {page.values.map((v, i) => (
                <Reveal key={v.title?.en || `value-${i}`} delay={i * 0.06} className="glass-card p-7">
                  <div className="w-10 h-10 grid place-items-center border border-cyan/50">
                    <span className="w-2.5 h-2.5 bg-cyan rotate-45" />
                  </div>
                  <h3 className="mt-5 font-display font-bold uppercase text-fg text-lg tracking-wide">
                    {pick(v.title, lang)}
                  </h3>
                  <p className="mt-3 text-mut text-sm leading-relaxed">{pick(v.body, lang)}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Stats */}
      {page?.stats?.length ? (
        <section className="container-x py-16" data-testid="about-stats">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
            {page.stats.map((s, i) => (
              <Reveal key={s.label?.en || `stat-${i}`} delay={i * 0.08} className="text-center">
                <div className="font-display font-bold text-accent text-[clamp(2.5rem,5vw,4rem)] leading-none">
                  <StatCounter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-2 font-techmono text-xs uppercase tracking-widest text-mut">{pick(s.label, lang)}</div>
              </Reveal>
            ))}
          </div>
        </section>
      ) : null}

      {/* Timeline */}
      <section className="bg-surface border-y border-hair py-20 md:py-28" data-testid="about-timeline">
        <div className="container-x">
          <SectionHeading eyebrow={t("about.timelineTitle")} title={t("about.timelineTitle")} />
          {timelineItems.length === 0 ? (
            <div className="mt-12"><EmptyState message={t("about.timelineEmpty")} icon={Clock} /></div>
          ) : (
            <div className="mt-14 relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-line md:-translate-x-1/2" />
              <div className="space-y-12">
                {timelineItems.map((tl, i) => {
                  const right = i % 2 === 1;
                  return (
                    <Reveal key={tl.id} delay={0.05} className="relative">
                      <div className={`md:grid md:grid-cols-2 md:gap-12 ${right ? "" : ""}`}>
                        <div className={`pl-12 md:pl-0 ${right ? "md:order-2 md:pl-12" : "md:text-right md:pr-12"}`}>
                          <div className="font-display font-bold text-accent text-3xl">{tl.year}</div>
                          <h3 className="mt-1 font-display font-bold uppercase text-fg text-xl tracking-wide">
                            {pick({ en: tl.titleEn, fr: tl.titleFr }, lang)}
                          </h3>
                          <p className="mt-2 text-mut text-sm leading-relaxed">
                            {pick({ en: tl.descriptionEn, fr: tl.descriptionFr }, lang)}
                          </p>
                        </div>
                      </div>
                      <span className="absolute left-4 md:left-1/2 top-1.5 w-3 h-3 bg-cyan -translate-x-1/2 rotate-45" />
                    </Reveal>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Team */}
      <section className="container-x py-20 md:py-28" data-testid="about-team">
        <SectionHeading eyebrow={t("about.teamSubtitle")} title={t("about.teamTitle")} />
        {teamItems.length === 0 ? (
          <div className="mt-12"><EmptyState message={t("about.teamEmpty")} icon={Users} /></div>
        ) : (
          <div className="mt-14 grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
            {teamItems.map((m, i) => (
              <Reveal key={m.id} delay={i * 0.06}>
                <TeamCard member={m} lang={lang} onOpen={setBio} />
              </Reveal>
            ))}
          </div>
        )}
        <Pagination
          page={team?.page || teamPage}
          pages={team?.pages || 1}
          onChange={(p) => setParams({ page: String(p) })}
          testIdPrefix="team-pagination"
        />
      </section>

      {/* Partners */}
      {awardItems.length ? (
        <section className="bg-surface border-y border-hair py-16" data-testid="about-partners">
          <div className="container-x">
            <SectionHeading title={t("about.partnersTitle")} align="center" />
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
              {awardItems.map((a) => {
                const inner = a.logoUrl ? (
                  <img src={mediaUrl(a.logoUrl, { w: 240, fmt: "webp", q: 80 })} alt={a.altText} loading="lazy" decoding="async" className="max-h-12 w-auto object-contain" />
                ) : (
                  <span className="font-display uppercase tracking-widest text-mut text-sm">{a.altText}</span>
                );
                return (
                  <div
                    key={a.id}
                    className="h-24 grid place-items-center border border-hair grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                    data-testid="partner-logo"
                  >
                    {a.linkUrl ? (
                      <a href={a.linkUrl} target="_blank" rel="noopener noreferrer">{inner}</a>
                    ) : inner}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      ) : null}

      {page ? <CTABanner title={pick(page.heroTitle, lang)} subtitle={pick(page.heroSubtitle, lang)} image={page.heroImageUrl} /> : null}

      {/* Bio modal */}
      <Dialog open={!!bio} onOpenChange={(o) => !o && setBio(null)}>
        <DialogContent className="bg-surface border-hair text-fg max-w-lg" data-testid="bio-modal">
          {bio ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-wide text-2xl text-fg">{bio.name}</DialogTitle>
                <p className="font-techmono text-xs uppercase tracking-wider text-accent">
                  {pick({ en: bio.titleEn, fr: bio.titleFr }, lang)}
                </p>
              </DialogHeader>
              <div className="mt-2 max-h-[50vh] overflow-y-auto">
                <RichText html={pick({ en: bio.bioEn, fr: bio.bioFr }, lang)} />
              </div>
              <div className="mt-4">
                <LinkedInButton url={bio.linkedinUrl} label={`${bio.name} LinkedIn`} />
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
